import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";

import { Search, Filter, Heart, MessageCircle, Star, Crown, Zap, Check } from "lucide-react";
import {
  GiAries, GiTaurus, GiGemini, GiCancer, GiLeo, GiVirgo,
  GiLibra, GiScorpio, GiSagittarius, GiCapricorn, GiAquarius, GiPisces,
} from "react-icons/gi";
import type { IconType } from "react-icons";

import romanticAvatar from "@/assets/avatar-romantic.jpg";
import { apiClient, isAuthSessionExpiredError } from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

interface ExploreProps {
  onNavigateToChat: (boyfriendId?: string) => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToCharacterCreation: () => void;
  onNavigateToExplore: () => void;
  onNavigateToPremium: (selectedBoyfriendPhoto?: string) => void;
  onSelectBoyfriend: (boyfriendId: string) => void;
  user?: {
    email?: string;
    name?: string;
    isPremium?: boolean;
  } | null;
  onPromptLogin?: () => void;
  onTokensClick?: () => void;
  userTokens?: number;
}

// Import BoyfriendProfile from API models
import { BoyfriendProfile, ZodiacKey } from '@/api/models';

const ZODIAC_ICONS: Record<ZodiacKey, IconType> = {
  aries: GiAries,
  taurus: GiTaurus,
  gemini: GiGemini,
  cancer: GiCancer,
  leo: GiLeo,
  virgo: GiVirgo,
  libra: GiLibra,
  scorpio: GiScorpio,
  sagittarius: GiSagittarius,
  capricorn: GiCapricorn,
  aquarius: GiAquarius,
  pisces: GiPisces,
};

export const Explore = ({ 
  onNavigateToChat, 
  onNavigateToMyBoyfriends, 
  onNavigateToProfile, 
  onNavigateToSettings, 
  onNavigateToCharacterCreation,
  onNavigateToExplore,
  onNavigateToPremium,
  onSelectBoyfriend,
  user, 
  onPromptLogin,
  onTokensClick,
  userTokens
}: ExploreProps) => {
  const { t } = useTranslation();
  
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'romantic' | 'badboy' | 'intellectual' | 'protector' | 'funny' | 'sporty'>('all');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [boyfriends, setBoyfriends] = useState<BoyfriendProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Load boyfriends from API
  const loadBoyfriends = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await apiClient.get('/boyfriends');
        
        // Interests are now already arrays from API
        const processedBoyfriends = response.map((boyfriend: any) => ({
          ...boyfriend,
          interests: Array.isArray(boyfriend.interests) ? boyfriend.interests : []
        }));
        
        setBoyfriends(processedBoyfriends);
      } catch (err: unknown) {
        console.error('Error loading boyfriends:', err);
        if (isAuthSessionExpiredError(err)) {
          return;
        }
        setError('Failed to load boyfriends. Please try again.');
        toast({
          title: "Error",
          description: "Failed to load boyfriends. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    loadBoyfriends();
  }, [toast]);

  const filteredBoyfriends = boyfriends.filter(boyfriend => {
    if (selectedFilter === 'all') return true;
    const p = boyfriend.personality || '';
    return p === `boyfriend_attr.personality.${selectedFilter}` || p.endsWith(`.${selectedFilter}`);
  });

  // Avatar URLs used directly from server

  const handleSelectBoyfriend = (boyfriendId: string) => {
    if (!user) {
      onPromptLogin?.();
      return;
    }
    
    // Find the boyfriend to check if they're premium
    const boyfriend = boyfriends.find(b => b.id === boyfriendId);
    
    // If user is not premium and boyfriend is premium
    if (!user.isPremium && boyfriend?.isPremium) {
      onNavigateToPremium?.(boyfriend.avatarUrl);
      return;
    }
    
    onSelectBoyfriend(boyfriendId);
  };

  const handleChatWithBoyfriend = (e: React.MouseEvent, boyfriendId: string) => {
    e.stopPropagation(); // Prevent card click event
    if (!user) {
      onPromptLogin?.();
      return;
    }
    
    // Find the boyfriend to check if they're premium
    const boyfriend = boyfriends.find(b => b.id === boyfriendId);
    
    // If user is not premium and boyfriend is premium
    if (!user.isPremium && boyfriend?.isPremium) {
      onNavigateToPremium?.(boyfriend.avatarUrl);
      return;
    }
    
    onNavigateToChat(boyfriendId);
  };

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: string) => {
    console.log('Purchasing subscription:', plan, paymentMethod);
    // Handle subscription purchase logic here
    setShowPaymentModal(false);
    setShowPremiumModal(false);
  };

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0" style={{ paddingTop: `calc(1rem + var(--safe-area-inset-top, 0px))` }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              {t('explore.title')}
            </h1>
          </div>
          {!user?.isPremium && (
            <Button variant="romantic" size="sm" onClick={() => onNavigateToPremium()}>
              <Crown className="h-4 w-4 mr-2" />
              {t('explore.upgrade')}
            </Button>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="px-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto pt-4">

          {/* Loading State - Skeleton Cards */}
          {isLoading && (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="relative h-64 rounded-lg overflow-hidden border border-primary/20">
                  <Skeleton className="absolute inset-0 rounded-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute inset-0 p-4 flex flex-col justify-between">
                    <div className="flex items-center gap-2">
                      <Skeleton className="w-2 h-2 rounded-full" />
                      <Skeleton className="h-3 w-10" />
                    </div>
                    <div className="pb-10 space-y-2">
                      <Skeleton className="h-6 w-28" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3">
                    <Skeleton className="h-8 w-24 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg mb-4">{error}</p>
              <Button 
                variant="outline" 
                onClick={loadBoyfriends}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Boyfriends Grid */}
          {!isLoading && !error && (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredBoyfriends.map((boyfriend) => {
                const ZodiacIcon = boyfriend.zodiacKey ? ZODIAC_ICONS[boyfriend.zodiacKey] : null;
                const zodiacLabel = boyfriend.zodiac;
                return (
                <Card 
                  key={boyfriend.id}
                  className="group cursor-pointer hover:scale-105 transition-all duration-300 border-primary/20 hover:border-primary/40 animate-fade-in overflow-hidden relative h-64"
                  onClick={() => handleSelectBoyfriend(boyfriend.id)}
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundImage: `url(${boyfriend.avatarUrl})` }}
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  
                  {/* Premium Badge */}
                  {boyfriend.isPremium && (
                    <div className="absolute top-3 right-3">
                      <div className="bg-amber-500/20 backdrop-blur-sm border border-amber-400/40 rounded-full p-2 flex items-center justify-center">
                        <Crown className="h-4 w-4 text-amber-300" />
                      </div>
                    </div>
                  )}
                  
                  {/* Content Overlay */}
                  <div className="absolute inset-0 p-4 flex flex-col justify-between text-white">
                    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 self-start">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-white/90 drop-shadow-lg">Online</span>
                    </div>
                    <div className="pb-10">
                      <h3 className="font-bold text-xl mb-1 drop-shadow-lg">{boyfriend.name}{boyfriend.age ? <span className="font-normal text-base">, {boyfriend.age}</span> : null}</h3>
                      {zodiacLabel && (
                        <p className="text-sm font-medium mb-2 text-primary-glow drop-shadow-lg flex items-center gap-1">
                          {ZodiacIcon && <ZodiacIcon className="h-3.5 w-3.5 opacity-70" />}
                          {zodiacLabel}
                        </p>
                      )}
                      <p className="text-xs text-white/80 mb-1">{boyfriend.occupation} • {boyfriend.location}</p>
                      
                    </div>
                  </div>
                  
                  {/* Chat Button - Bottom Right */}
                  <div className="absolute bottom-3 right-3">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="bg-pink-500/20 hover:bg-gradient-to-r hover:from-pink-500 hover:to-purple-500 text-white border border-pink-400/40 hover:border-pink-300 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 hover:scale-125 text-xs hover:shadow-lg hover:shadow-pink-500/50"
                      onClick={(e) => handleChatWithBoyfriend(e, boyfriend.id)}
                    >
                      <MessageCircle className="h-3 w-3 mr-1" />
                      {t('explore.chat_now')}
                    </Button>
                  </div>
                </Card>
                );
              })}
            </div>
          )}

          {/* No Results */}
          {!isLoading && !error && filteredBoyfriends.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                {t('explore.no_results')}
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => {
                  setSelectedFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Premium Upgrade Modal */}
      <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Premium Upgrade</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Title */}
            <h2 className="text-center text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              {t('premium_modal.unlock_all')}
            </h2>
            
            {/* Urgency Banner */}
            <div className="border-2 border-orange-500 bg-transparent text-orange-500 p-3 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2">
                <span className="text-lg">⏰</span>
                <span className="font-bold text-sm">{t('premium_modal.few_hours_left')}</span>
              </div>
            </div>

            {/* Features Section */}
            <Card className="p-4 bg-gradient-romantic text-white border-0">
              <div className="text-center mb-4">
                <Zap className="h-8 w-8 mx-auto mb-2" />
                <h2 className="text-lg font-bold mb-2">{t('premium_modal.premium_features')}</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unblurred_photos')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.hd_images')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.priority_responses')}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.free_tokens')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.generate_spicy')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unlock_premium_bfs')}</span>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Pricing Plans */}
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 ${
                  selectedPlan === 'monthly' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.monthly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$19.99</p>
                  <p className="text-2xl font-bold text-primary">$9.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_month')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50')}</p>
                <ul className="text-[10px] text-muted-foreground space-y-1 mb-3">
                  <li>{t('premium_modal.all_premium_features')}</li>
                  <li>{t('premium_modal.cancel_anytime')}</li>
                  <li>{t('premium_modal.free_trial')}</li>
                </ul>
                {selectedPlan === 'monthly' && (
                  <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>
                )}
              </Card>

              <Card 
                className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 relative ${
                  selectedPlan === 'annual' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('annual')}
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap">
                    {t('premium_modal.best_value')}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.yearly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$159.99</p>
                  <p className="text-2xl font-bold text-primary">$79.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_year')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50_plus')}</p>
                <ul className="text-[10px] text-muted-foreground space-y-1 mb-3">
                  <li>{t('premium_modal.all_premium_features')}</li>
                  <li>{t('premium_modal.two_months_free')}</li>
                  <li>{t('premium_modal.priority_support')}</li>
                </ul>
                {selectedPlan === 'annual' && (
                  <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>
                )}
              </Card>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={() => {
                  setShowPremiumModal(false);
                  setShowPaymentModal(true);
                }}
                variant="romantic" 
                size="lg" 
                className="w-full py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('premium_modal.upgrade_to_plan', { plan: selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan') })}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                {t('premium_modal.secure_payment')}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <SubscriptionPaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        selectedPlan={selectedPlan === 'annual' ? 'yearly' : 'monthly'}
        onPurchaseSubscription={handlePurchaseSubscription}
      />

      
    </div>
  );
};
